import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Container } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from '../contexts/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorageIcon from '@mui/icons-material/Storage';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionIcon from '@mui/icons-material/Description';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [monitorStatus, setMonitorStatus] = useState<'online' | 'offline' | null>(null);

  useEffect(() => {
    let cancelled = false;
    const checkStatus = () => {
      const token = localStorage.getItem('token');
      axios
        .get(`${process.env.REACT_APP_API_URL}/admin/monitor/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        .then((res) => {
          if (!cancelled) setMonitorStatus(res.data?.data?.status === 'online' ? 'online' : 'offline');
        })
        .catch(() => {
          if (!cancelled) setMonitorStatus('offline');
        });
    };
    checkStatus();
    const interval = setInterval(checkStatus, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!isAuthenticated) {
    return null;
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{ 
        bgcolor: '#202c33',
        borderBottom: '1px solid',
        borderColor: '#374045',
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: {
            lg: '1200px',
            xl: '1400px'
          }
        }}
      >
        <Toolbar disableGutters>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Box component="img" src="/logo512.png" alt="WuConnect" sx={{ width: 32, height: 32, borderRadius: '50%', mr: 1 }} />
            <Typography 
              variant="h6" 
              component={RouterLink} 
              to="/"
              sx={{ 
                color: '#e9edef',
                textDecoration: 'none',
                fontWeight: 500,
                letterSpacing: '0.5px',
              }}
            >
              WuConnect
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<DashboardIcon />}
              sx={{
                color: isActive('/') ? '#820AD1' : '#8696a0',
                '&:hover': {
                  color: '#820AD1',
                },
                minWidth: '120px',
              }}
            >
              Dashboard
            </Button>
            <Button
              component={RouterLink}
              to="/instances"
              startIcon={<StorageIcon />}
              sx={{
                color: isActive('/instances') ? '#820AD1' : '#8696a0',
                '&:hover': {
                  color: '#820AD1',
                },
                minWidth: '120px',
              }}
            >
              Instâncias
            </Button>
            <Button
              component={RouterLink}
              to="/docs"
              startIcon={<DescriptionIcon />}
              sx={{
                color: isActive('/docs') ? '#820AD1' : '#8696a0',
                '&:hover': {
                  color: '#820AD1',
                },
                minWidth: '120px',
              }}
            >
              API Docs
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 2,
              cursor: 'default',
            }}
          >
            <FiberManualRecordIcon
              sx={{
                fontSize: 14,
                color:
                  monitorStatus === null
                    ? '#8696a0'
                    : monitorStatus === 'online'
                    ? '#4caf50'
                    : '#ea4335',
                mr: 0.5,
              }}
            />
            <Typography variant="body2" sx={{ color: '#8696a0' }}>
              Monitor {monitorStatus === null ? '...' : monitorStatus === 'online' ? 'Online' : 'Offline'}
            </Typography>
          </Box>

          <Button
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              color: '#8696a0',
              '&:hover': {
                color: '#ea4335',
              },
            }}
          >
            Sair
          </Button>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 